
import { supabase } from "@/integrations/supabase/client";
import { ExperienceFormData } from "@/components/assessment/ExperienceSection";

export interface AssessmentScores {
  productCraft?: { score: number; responses: Record<string, number> };
  customerDiscovery?: { score: number; responses: Record<string, number> };
  researchDesign?: { score: number; responses: Record<string, number> };
  marketImpact?: { score: number; responses: Record<string, number> };
  productOperations?: { score: number; responses: Record<string, number> };
  industryImpact?: { score: number; responses: Record<string, number> };
}

// Calculate total assessment score (0-120 scale)
export const calculateTotalScore = (assessmentData: AssessmentScores): number => {
  console.log("Calculating total score with data:", assessmentData);
  
  const scores = {
    productCraft: Math.min(30, assessmentData.productCraft?.score || 0),
    customerDiscovery: Math.min(20, assessmentData.customerDiscovery?.score || 0),
    researchDesign: Math.min(20, assessmentData.researchDesign?.score || 0),
    marketImpact: Math.min(20, assessmentData.marketImpact?.score || 0),
    productOperations: Math.min(15, assessmentData.productOperations?.score || 0),
    industryImpact: Math.min(15, assessmentData.industryImpact?.score || 0)
  };

  console.log("Individual scores after capping:", scores);
  const total = Math.min(120, Object.values(scores).reduce((total, score) => total + score, 0));
  console.log("Total score calculated (capped at 120):", total);
  
  return Math.round(total);
};

export const calculateExperienceScore = (data: ExperienceFormData): number => {
  let score = 0;
  
  // Strongest attribute (1-3 points)
  if (data.strongestAttribute) {
    score += 2;
  }
  
  // Years experience (1-5 points)
  switch (data.yearsExperience) {
    case '0-3':
      score += 1;
      break;
    case '3-10':
      score += 3;
      break;
    case '10+':
      score += 5;
      break;
  }
  
  // Company types (up to 3 points)
  score += Math.min(3, data.companyTypes.length);
  
  // Industries (up to 3 points)
  score += Math.min(3, data.industries.length);
  
  // Product lifecycles (up to 3 points)
  score += Math.min(3, data.productLifecycles.length);
  
  // Products in development (0-3 points)
  const inDevelopment = parseInt(data.productsInDevelopment) || 0;
  score += Math.min(3, inDevelopment);
  
  // Products launched (0-5 points)
  const launched = parseInt(data.productsLaunched) || 0;
  score += Math.min(5, launched);
  
  return Math.min(20, score);
};

export const getCareerStage = (score: number) => {
  console.log("Determining career stage for score:", score);
  
  if (score <= 35) return {
    stage: 'Explorer',
    description: "You're building a solid foundation in product management. You're developing core skills in execution, stakeholder management, and understanding customer needs. You work effectively within established processes and contribute to feature development. Your focus is on mastering the fundamentals while delivering value within defined parameters."
  };
  if (score <= 65) return {
    stage: 'Achiever',
    description: "You're a capable product manager who reliably delivers results. You lead product initiatives with increasing independence and make meaningful contributions to product strategy. You're developing deeper expertise in specific domains and beginning to influence others through your approach to product challenges. You're becoming known for particular strengths within your organization."
  };
  if (score <= 89) return {
    stage: 'Specialist',
    description: "You've developed substantial expertise and drive significant product outcomes. You lead important product efforts and have mastered multiple facets of product management. Your influence extends beyond your immediate product area, and you're shaping how teams approach product development. You might be leading other product managers or mentoring more junior colleagues."
  };
  if (score <= 109) return {
    stage: 'Authority',
    description: "You significantly influence organizational direction through your product leadership. You create frameworks and approaches that others adopt across the company. Your strategic thinking extends beyond individual products to portfolio or company-level impacts. Whether as a senior IC or people leader, your perspective shapes important company decisions, and you develop other product professionals into stronger leaders."
  };
  return {
    stage: 'Guru',
    description: "You define what product excellence looks like within your organization and beyond. You create product practices that become industry standards. Your influence extends well beyond your company, and you're known for pioneering approaches that advance the entire profession. You develop other product leaders who themselves become authorities. Your work shapes how the market views successful product leadership."
  };
};

export const saveAssessmentResults = async (userId: string, scores: AssessmentScores) => {
  console.log("Saving assessment results for user:", userId);
  console.log("Scores to save:", scores);
  
  const totalScore = calculateTotalScore(scores);
  console.log("Calculated total score:", totalScore);

  // Get user profile to check if they're a test profile
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', userId)
    .single();

  if (profileError) {
    console.error('Error fetching profile:', profileError);
    throw profileError;
  }

  const isTestProfile = profile.role === 'test_user';
  const nextAssessmentDate = isTestProfile 
    ? null 
    : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();

  // Update profile with new score
  const { data: profileData, error: profileError2 } = await supabase
    .from('profiles')
    .update({
      total_score: totalScore,
      last_assessment_date: new Date().toISOString(),
      career_level: getCareerStage(totalScore).stage.toLowerCase(),
      assessment_completed: true,
      next_assessment_date: nextAssessmentDate
    })
    .eq('id', userId)
    .select();

  if (profileError2) {
    console.error('Error updating profile:', profileError2);
    throw profileError2;
  }

  console.log("Profile updated with new score:", profileData);

  // Update or create assessment guidance
  const { data: existingGuidance } = await supabase
    .from('assessment_guidance')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  const careerStage = getCareerStage(totalScore);
  const nextStage = totalScore < 110 ? getCareerStage(totalScore + 20).stage : null;
  const pointsNeeded = nextStage ? (totalScore < 35 ? 36 - totalScore : 
                                  totalScore < 65 ? 66 - totalScore :
                                  totalScore < 89 ? 90 - totalScore :
                                  totalScore < 109 ? 110 - totalScore : 0) : 0;

  if (existingGuidance?.id) {
    await supabase
      .from('assessment_guidance')
      .update({
        current_level: careerStage.stage,
        next_level: nextStage || careerStage.stage,
        points_needed: pointsNeeded,
        updated_at: new Date().toISOString()
      })
      .eq('id', existingGuidance.id);
  } else {
    const { data: guidance, error: guidanceError } = await supabase
      .from('assessment_guidance')
      .insert({
        user_id: userId,
        current_level: careerStage.stage,
        next_level: nextStage || careerStage.stage,
        points_needed: pointsNeeded
      })
      .select()
      .single();

    if (guidanceError) {
      console.error('Error creating guidance:', guidanceError);
      throw guidanceError;
    }

    // Create initial suggested actions
    const suggestedActions = [
      { action_text: `Complete ${careerStage.stage} level assessment`, points_value: 20 },
      { action_text: 'Document recent achievements', points_value: 15 },
      { action_text: 'Update career artifacts', points_value: 10 }
    ];

    await supabase
      .from('suggested_actions')
      .insert(suggestedActions.map(action => ({
        guidance_id: guidance.id,
        ...action
      })));
  }

  // Save individual skills
  const skillsToInsert = [
    {
      name: 'Product Craft & Technical Excellence',
      category: 'Technical Skills',
      current_level: Math.ceil((scores.productCraft?.score || 0) / 6),
      user_id: userId
    },
    {
      name: 'Customer Discovery & Understanding',
      category: 'Core Skills',
      current_level: Math.ceil((scores.customerDiscovery?.score || 0) / 4),
      user_id: userId
    },
    {
      name: 'Research & Design Mastery',
      category: 'Technical Skills',
      current_level: Math.ceil((scores.researchDesign?.score || 0) / 4),
      user_id: userId
    },
    {
      name: 'Market & Business Impact',
      category: 'Business Skills',
      current_level: Math.ceil((scores.marketImpact?.score || 0) / 4),
      user_id: userId
    },
    {
      name: 'Product Operations & Team Leadership',
      category: 'Leadership Skills',
      current_level: Math.ceil((scores.productOperations?.score || 0) / 3),
      user_id: userId
    },
    {
      name: 'Industry Impact & Influence',
      category: 'Industry Skills',
      current_level: Math.ceil((scores.industryImpact?.score || 0) / 3),
      user_id: userId
    }
  ];

  const { error: skillsError } = await supabase
    .from('skills')
    .upsert(skillsToInsert, {
      onConflict: 'user_id,name'
    });

  if (skillsError) {
    console.error('Error updating skills:', skillsError);
    throw skillsError;
  }

  // Create career progress milestone
  const { error: progressError } = await supabase
    .from('career_progress')
    .insert({
      user_id: userId,
      milestone_type: 'assessment',
      milestone_name: `Completed Product Leadership Assessment - ${careerStage.stage}`,
      points: totalScore
    });

  if (progressError) {
    console.error('Error creating career progress:', progressError);
    throw progressError;
  }

  console.log("Assessment results saved successfully");
  return { success: true, totalScore };
};
